// ============================================================================
// SERVICE CATEGORY SERVICE — entity-scoped via X-Entity-ID.
// ============================================================================

import { get, post, put, del } from '../client/axiosClient';
import { serviceCategoryEndpoints } from '../endpoints/commercial.endpoints';
import { buildEndpointWithQuery } from '../endpoints/base.endpoints';
import type {
  ApiResponse, BackendPagedResult, PaginatedResponse, EmptyResponse, ID,
  ServiceCategoryDto, CreateServiceCategoryRequest, UpdateServiceCategoryRequest, ServiceCategoryQueryParams,
} from '../types';

class ServiceCategoryService {
  async getAll(params?: ServiceCategoryQueryParams): Promise<PaginatedResponse<ServiceCategoryDto>> {
    const endpoint = params ? buildEndpointWithQuery(serviceCategoryEndpoints.list(), params) : serviceCategoryEndpoints.list();
    const response = await get<ApiResponse<BackendPagedResult<ServiceCategoryDto>>>(endpoint);
    const paged = response.data.data;
    return {
      success: response.data.success, message: response.data.message,
      data: Array.from(paged.items || []),
      pagination: {
        currentPage: paged.page, pageSize: paged.pageSize, totalPages: paged.totalPages,
        totalItems: paged.totalCount, hasNextPage: paged.hasNext, hasPreviousPage: paged.hasPrevious,
      },
    };
  }

  async getById(id: ID): Promise<ServiceCategoryDto> {
    const response = await get<ApiResponse<ServiceCategoryDto>>(serviceCategoryEndpoints.getById(id));
    return response.data.data;
  }

  async create(data: CreateServiceCategoryRequest): Promise<ServiceCategoryDto> {
    const response = await post<ApiResponse<ServiceCategoryDto>>(serviceCategoryEndpoints.create(), data);
    return response.data.data;
  }

  async update(id: ID, data: UpdateServiceCategoryRequest): Promise<ServiceCategoryDto> {
    const response = await put<ApiResponse<ServiceCategoryDto>>(serviceCategoryEndpoints.update(id), data);
    return response.data.data;
  }

  async delete(id: ID, rowVersion: number): Promise<EmptyResponse> {
    const url = buildEndpointWithQuery(serviceCategoryEndpoints.delete(id), { rowVersion });
    const response = await del<EmptyResponse>(url);
    return response.data;
  }

  async isCodeAvailable(code: string, excludeId?: ID): Promise<boolean> {
    const endpoint = serviceCategoryEndpoints.validateCode(code);
    const url = excludeId ? buildEndpointWithQuery(endpoint, { excludeId }) : endpoint;
    const response = await get<ApiResponse<boolean>>(url);
    return response.data.data;
  }
}

export const serviceCategoryService = new ServiceCategoryService();
export default serviceCategoryService;
